// src/components/events/CreateEventFromTemplate.tsx


// src/components/events/CreateEventFromTemplate.tsx
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
 collection,
 addDoc,
 updateDoc,
 serverTimestamp,
 doc,
 getDocs,
 query,
 where,
 getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import TemplateSelector from './TemplateSelector';
import MusicSelector from './MusicSelector';
import { musicLibraries } from './MusicSelector';
import VideoThemePlayer from '../player/VideoThemePlayer';
import { TemplateType } from '../../types/Template';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ChooseRecipients from './ChooseRecipients';
import GuestList from './GuestList';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkInput from '../common/SparkInput';
import SparkHeading from '../common/SparkHeading';
import SparkFooter from '../common/SparkFooter';
import TipCard from '../common/TipCard';
import MagicalPreviewDisplay from '../common/MagicalPreviewDisplay';
import './CreateEvent.css';
import yourPictureHere from '../layouts/images/yourpicturehere.png';
import sparkInstructionsVideo from '../layouts/images/sparkinstructions2.mp4';
import FancyInvitationPreview from '../player/FancyInvitationPreview';


// Add new imports for sales modal and paywall integration
import UnlockSalesModal from '../billing/UnlockSalesModal';
import useEventPaywall from '../../hooks/useEventPaywall';
import UnlockPaywallButton from '../billing/UnlockPaywallButton';


const createStableKey = (templateId: string | null, musicId: string | null, imageUrl: string, videoUrl: string) => {
 return `${templateId || 'no-template'}-${musicId || 'no-music'}-${imageUrl ? 'has-image' : 'no-image'}-${videoUrl ? 'has-video' : 'no-video'}`;
};


// Collapsed step machine: Step 2 does read-only â†’ flip-to-edit + modals
enum Step {
 TemplateSelection = 1,
 TemplatePreview,        // persuasion â†’ flip â†’ edit panel + modals
 Review,                 // Review & Create
 AddGuests,              // (formerly ChooseRecipients; occurs AFTER creation)
 SendInvitations,        // paywall / share / send
 ReminderSettings,
 Registration,
}


// Utility â€” read video duration before accepting it
const getVideoDuration = (file: File): Promise<number> =>
 new Promise((resolve, reject) => {
   const tempVideo = document.createElement('video');
   tempVideo.preload = 'metadata';


   tempVideo.onloadedmetadata = () => {
     URL.revokeObjectURL(tempVideo.src);
     resolve(tempVideo.duration);
   };


   tempVideo.onerror = () => reject(new Error('Unable to load video metadata'));
   tempVideo.src = URL.createObjectURL(file);
 });


// Helper function to format date without timezone issues
const formatEventDate = (dateString: string): string => {
 if (!dateString) return '';
  // Parse the date string and create date at noon to avoid timezone issues
 const [year, month, day] = dateString.split('-').map(Number);
 const date = new Date(year, month - 1, day, 12, 0, 0);
  const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
 const monthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                     'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  const dayOfWeek = dayNames[date.getDay()];
 const monthName = monthNames[date.getMonth()];
 const dayNum = date.getDate();
  // Add ordinal suffix
 const suffix = ['TH', 'ST', 'ND', 'RD'][
   dayNum % 10 > 3 || Math.floor(dayNum / 10) === 1 ? 0 : dayNum % 10
 ];
  return `${dayOfWeek} ${monthName} ${dayNum}${suffix}`;
};


// --- lightweight modal shell ---
const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => {
 return (
   <div className="spark-modal-overlay" role="dialog" aria-modal="true">
     <div className="spark-modal">
       <div className="spark-modal-header">
         <h3>{title}</h3>
         <button className="spark-modal-close" onClick={onClose} aria-label="Close">âœ•</button>
       </div>
       <div className="spark-modal-body">{children}</div>
     </div>
   </div>
 );
};


// --- Image modal ---
const MediaImageModal: React.FC<{
 initialPreview: string | null;
 onChange: (file: File, previewUrl: string) => void;
 onClose: () => void;
}> = ({ initialPreview, onChange, onClose }) => {
 const [preview, setPreview] = useState<string | null>(initialPreview);


 return (
   <ModalShell title="Add Photo" onClose={onClose}>
     <div className="media-modal">
       {preview ? <img className="media-preview" src={preview} alt="Photo preview" /> : <div className="media-placeholder">No image</div>}
       <input
         type="file"
         accept="image/*"
         onChange={(e) => {
           const file = e.target.files?.[0];
           if (!file) return;
           if (!file.type.startsWith('image/')) return alert('Please select an image.');
           if (file.size > 5 * 1024 * 1024) return alert('Image must be < 5 MB.');
           const r = new FileReader();
           r.onloadend = () => {
             const url = r.result as string;
             setPreview(url);
             onChange(file, url);
           };
           r.readAsDataURL(file);
         }}
       />
       <div className="spark-modal-actions">
         <SparkButton variant="secondary" onClick={onClose}>Done</SparkButton>
       </div>
     </div>
   </ModalShell>
 );
};


// --- Video modal (15s duration check reused) ---
const MediaVideoModal: React.FC<{
 initialPreview: string | null;
 onChange: (file: File, previewUrl: string) => void;
 onValidateDuration: (f: File) => Promise<number>;
 onClose: () => void;
}> = ({ initialPreview, onChange, onValidateDuration, onClose }) => {
 const [preview, setPreview] = useState<string | null>(initialPreview);


 return (
   <ModalShell title="Add Video" onClose={onClose}>
     <div className="media-modal">
       {preview ? <video className="media-preview" src={preview} controls /> : <div className="media-placeholder">No video</div>}
       <input
         type="file"
         accept="video/*"
         onChange={async (e) => {
           const file = e.target.files?.[0];
           if (!file) return;
           if (!file.type.startsWith('video/')) return alert('Please select a video.');
           if (file.size > 50 * 1024 * 1024) return alert('Video must be < 50 MB.');
           try {
             const d = await onValidateDuration(file);
             if (d > 15.0) return alert('Video must be 15s or shorter.');
           } catch {
             return alert('Could not read video metadata.');
           }
           const url = URL.createObjectURL(file);
           setPreview(url);
           onChange(file, url);
         }}
       />
       <div className="spark-modal-actions">
         <SparkButton variant="secondary" onClick={onClose}>Done</SparkButton>
       </div>
     </div>
   </ModalShell>
 );
};


// --- Music picker modal (wrap your existing MusicSelector) ---
const MusicSelectModal: React.FC<{
 templateId: string | null;
 selectedMusicId: string | null;
 onSelect: (id: string | null, url: string, name: string) => void;
 onClose: () => void;
}> = ({ templateId, selectedMusicId, onSelect, onClose }) => {
 return (
   <ModalShell title="Choose Background Music" onClose={onClose}>
     <div className="music-modal">
       <MusicSelector
         templateId={templateId}
         selectedMusicId={selectedMusicId}
         onSelectMusic={(id, url, name) => {
           onSelect(id, url, name);
           onClose();
         }}
       />
     </div>
   </ModalShell>
 );
};


const CreateEventFromTemplate: React.FC = () => {
 const { user, isAnonymous, convertAnonymousAccount, login } = useAuth();
 const navigate = useNavigate();
 const location = useLocation();
 const [searchParams] = useSearchParams();
 const editEventId = searchParams.get('eventId');
 const urlTemplateId = searchParams.get('templateId');       // From ?templateId=
 const initialStep   = searchParams.get('step');             // From ?step=
 const urlSearchTerm = searchParams.get('search') || '';     // From ?search=
 const isEditMode = !!editEventId;
 const [originalEventData, setOriginalEventData] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState<Step>(() => {
   if (initialStep) {
     const stepNum = parseInt(initialStep);
     // Validate that step is within valid range (1-7)
     // If step is invalid or for viewing invitation (step 9), go to TemplatePreview (step 2) when editing
     if (stepNum > 7 || stepNum < 1) {
       return editEventId ? Step.TemplatePreview : Step.TemplateSelection;
     }
     return stepNum;
   }
  
   // NEW: Skip to preview if templateId provided
   if (urlTemplateId) {
     return Step.TemplatePreview;
   }
  
   return Step.TemplateSelection;
 });
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);


 // Add sales modal state
 const [isSalesModalOpen, setIsSalesModalOpen] = useState(false);


 // Store the created event ID
 const [createdEventId, setCreatedEventId] = useState<string | null>(null);


 // Add paywall hook at top level to avoid React hooks rule violations
 const eventId = createdEventId || '';
 const { unlocked, loading: paywallLoading } = useEventPaywall(eventId);


 // === Phone preview height so the right card matches it ===
 const phoneRef = useRef<HTMLDivElement | null>(null);
 const [phoneHeight, setPhoneHeight] = useState<number>(640);


 useLayoutEffect(() => {
   if (!phoneRef.current) return;
   const el = phoneRef.current;


   // Type cast to keep TS quiet in older setups
   const ro = new (window as any).ResizeObserver((entries: any[]) => {
     for (const e of entries) {
       const h = Math.max(560, Math.round(e.contentRect.height));
       setPhoneHeight(h);
     }
   });


   ro.observe(el);


   // initial measure
   setTimeout(() => {
     const h = Math.max(560, el.offsetHeight || 0);
     setPhoneHeight(h);
   }, 0);


   return () => ro.disconnect();
 }, []);


 // Flip & modal state for the new Step 2
 const [rightSideFlipped, setRightSideFlipped] = useState(false);
 const [isPhotoModalOpen, setPhotoModalOpen] = useState(false);
 const [isVideoModalOpen, setVideoModalOpen] = useState(false);
 const [isMusicModalOpen, setMusicModalOpen] = useState(false);


 // Helper to set error and scroll to top
 const raiseError = (msg: string) => {
   setError(msg);
   window.scrollTo({ top: 0, behavior: 'smooth' });
 };


 /* â”€â”€â”€ open fullâ€‘screen preview from TemplatePreview step â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
 const handleTemplatePreviewInNewTab = () => {
   if (!activeTemplate) return;


   // Mute the current tab's music before opening new tab
   const bg = (window as any).bgMusic as HTMLAudioElement | undefined;
   if (bg && !bg.paused) {
     bg.pause();
     setPreviewMuted(true);
   }


   /* NEW â–¸ broadcast "mute yourself" to any fancy preview iframe */
   window.postMessage({ type: 'MUTE_PREVIEW_AUDIO' }, '*');


   /* Strip template to the bare fields the preview route needs */
   const { id, name, overlayBundle, version } = activeTemplate;
   const safeTemplate = { id, name, overlayBundle, version };


   /* Use whatever customData you already assembled for the mini preview */
   const previewData = {
     ...customData,
     recipientName: 'HEY YOU!',      // placeholder until host customises
   };


   const tab = window.open('/preview-invitation', '_blank');
   if (!tab) return;


   const relay = (e: MessageEvent<string>) => {
     if (e.origin !== window.location.origin) return;
     if (e.data === 'REQUEST_PREVIEW_DATA') {
       tab.postMessage(
         { type: 'PREVIEW_DATA', data: previewData, template: safeTemplate },
         window.location.origin
       );
       window.removeEventListener('message', relay);
     }
   };
   window.addEventListener('message', relay);
 };


 // Basic info with placeholder defaults
 const [title, setTitle] = useState('');
 const [description, setDescription] = useState('');
 const [eventDate, setEventDate] = useState('');
 const [startTime, setStartTime] = useState('');
 const [endTime, setEndTime] = useState('');
 const [location_state, setLocation_state] = useState('');
 const [reminderDate, setReminderDate] = useState('');


 // New state for image and video uploads
 const [mainImage, setMainImage] = useState<File | null>(null);
 const [mainImagePreview, setMainImagePreview] = useState<string | null>(null);
 const [mainImageUrl, setMainImageUrl] = useState<string>('');
  const [eventVideo, setEventVideo] = useState<File | null>(null);
 const [eventVideoPreview, setEventVideoPreview] = useState<string | null>(null);
 const [eventVideoUrl, setEventVideoUrl] = useState<string>('');


 // Template selection
 const [templateId, setTemplateId] = useState<string | null>(null);
 const [templateProjectId, setTemplateProjectId] = useState<string>('');


 // Template data for preview
 const [activeTemplate, setActiveTemplate] = useState<TemplateType | null>(null);
 const [previewKey, setPreviewKey] = useState<string>('');


 // Music selection state
 const [selectedMusicId, setSelectedMusicId] = useState<string | null>(null);
 const [selectedMusicUrl, setSelectedMusicUrl] = useState<string>('');
 const [selectedMusicName, setSelectedMusicName] = useState<string>('');
 const [previewMuted, setPreviewMuted] = useState(false);


 // Template customization with placeholder data
 const [customData, setCustomData] = useState<Record<string, any>>({
   eventTitle: 'YOUR EVENT!',
   address: 'Super Cool Party Town',
   sTime: '12:00 pm',
   eTime: '2:00 pm',
   eventDate: 'SATURDAY JANUARY 25TH',
   mainImageUrl: yourPictureHere,
   eventVideoUrl: sparkInstructionsVideo,
   rsvp_n: "https://emojiisland.com/products/sad-face-emoji-icon",
   rsvp_y: "https://emojiisland.com/products/party-face-emoji?_pos=5&_sid=11385593b&_ss=r",
 });


 // Recipients
 const [recipients, setRecipients] = useState<
   Array<{ name: string; email?: string; phone?: string }>
 >([]);


 // Track preview readiness
 const [isPreviewReady, setIsPreviewReady] = useState(false);


 // Reminder settings
 const [reminderNoResponse, setReminderNoResponse] = useState('');
 const [reminderAll, setReminderAll] = useState('');


 // Registration fields
 const [regEmail, setRegEmail] = useState('');
 const [regPassword, setRegPassword] = useState('');
 const [regDisplayName, setRegDisplayName] = useState('');
 const [isLoginMode, setIsLoginMode] = useState(false);
 const functions = getFunctions();


 // State for the invitation link
 const [anonymousInvitationLink, setAnonymousInvitationLink] = useState<string>('');


 // Comprehensive audio/video cleanup function
 const stopAllMediaPlayback = () => {
   // 1. Stop window.bgMusic if it exists
   const bgMusic = (window as any).bgMusic as HTMLAudioElement | undefined;
   if (bgMusic) {
     bgMusic.pause();
     bgMusic.currentTime = 0;
     bgMusic.src = '';
   }
  
   // 2. Stop ALL audio elements on the page
   const allAudioElements = document.querySelectorAll('audio');
   allAudioElements.forEach(audio => {
     audio.pause();
     audio.currentTime = 0;
     audio.src = '';
   });
  
   // 3. Stop ALL video elements on the page
   const allVideoElements = document.querySelectorAll('video');
   allVideoElements.forEach(video => {
     video.pause();
     video.currentTime = 0;
     video.src = '';
   });
  
   // 4. Call destroyInvite if it exists
   if (typeof (window as any).destroyInvite === 'function') {
     (window as any).destroyInvite();
   }
  
   // 5. Clear any audio context
   if ((window as any).audioContext) {
     (window as any).audioContext.close();
     (window as any).audioContext = null;
   }
  
   // 6. Force garbage collection hint
   (window as any).bgMusic = null;


   // 7. Remove the global mute button, if it exists
   const muteBtn = document.getElementById('global-mute-toggle');
   if (muteBtn) muteBtn.remove();
  
   setPreviewMuted(true);
 };
  
 // Add this effect to manage body class
 useEffect(() => {
   if (currentStep === Step.TemplatePreview) {
     document.documentElement.classList.add('template-selector-page');
     document.body.classList.add('template-selector-page');
     document.body.classList.add('invitation-preview-page');
   } else {
     stopAllMediaPlayback();
    
     document.documentElement.classList.remove('template-selector-page');
     document.body.classList.remove('template-selector-page');
     document.body.classList.remove('invitation-preview-page');
   }
  
   return () => {
     stopAllMediaPlayback();
     document.body.classList.remove('invitation-preview-page');
   };
 }, [currentStep]);


 // â”€â”€â”€ Navigation helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
 const goNext = () => {
   if (currentStep === Step.TemplatePreview) {
     stopAllMediaPlayback();
     setPreviewKey('');
     setIsPreviewReady(false);
   }


   setCurrentStep(currentStep + 1);
 };


 const goBack = () => {
   if (currentStep === Step.TemplatePreview) {
     stopAllMediaPlayback();
     setPreviewKey('');
     setIsPreviewReady(false);
   }


   setCurrentStep(currentStep - 1);
 };


 // Handlers
 const handleSelectTemplate = (selectedTemplateId: string, projectId: string) => {
   setTemplateId(selectedTemplateId);
   setTemplateProjectId(projectId);
    /* -------------------------------------------------------------
      Pick the first track in this template's library as a placeholder
   ------------------------------------------------------------- */
   const libraryKey = `template-${selectedTemplateId}`;
   const firstTrack = musicLibraries[libraryKey]?.[0];
    if (firstTrack) {
     // Store in dedicated music state
     setSelectedMusicId(firstTrack.id);
     setSelectedMusicUrl(firstTrack.url);
     setSelectedMusicName(firstTrack.name);
      // â€¦and also in customData so <VideoThemePlayer> can find it
     setCustomData(prev => ({
       ...prev,
       musicId: firstTrack.id,
       musicUrl: firstTrack.url,
       musicName: firstTrack.name,
     }));
   } else {
     // If this theme has no songs, clear any previous placeholder
     setSelectedMusicId(null);
     setSelectedMusicUrl('');
     setSelectedMusicName('');
     setCustomData(prev => ({
       ...prev,
       musicId: '',
       musicUrl: '',
       musicName: '',
     }));
   }
    // Jump straight to the preview step
   goNext();
 }; 


 const handleCustomDataChange = (newData: Record<string, any>) => {
   setCustomData(newData);
 };


 // Upload file to Firebase Storage
 const uploadFileToStorage = async (file: File, _path: string): Promise<string> => {
   try {
     if (!user || !user.uid) {
       throw new Error('User must be logged in to upload files');
     }
    
     const folderPath = isAnonymous ? 'public' : `events/${user.uid}`;
     const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
     const fullPath = `${folderPath}/${fileName}`;
    
     console.log(`Uploading file to path: ${fullPath}`);
    
     const storageRef = ref(storage, fullPath);
     await uploadBytes(storageRef, file);
     const downloadUrl = await getDownloadURL(storageRef);
    
     console.log(`File uploaded successfully. Download URL: ${downloadUrl}`);
     return downloadUrl;
   } catch (error) {
     console.error('Error uploading file to Firebase Storage:', error);
     throw error;
   }
 };


 // Copy invitation link to clipboard
 const handleCopyInvitationLink = async () => {
   if (!anonymousInvitationLink) return;
  
   let fullLink: string;
  
   if (!anonymousInvitationLink.startsWith('http')) {
     fullLink = `${window.location.origin}${anonymousInvitationLink}`;
   } else if (anonymousInvitationLink.startsWith(window.location.origin)) {
     fullLink = anonymousInvitationLink;
   } else {
     const u = new URL(anonymousInvitationLink);
     fullLink = `${window.location.origin}${u.pathname}${u.search}`;
   }
  
   try {
     await navigator.clipboard.writeText(fullLink);
     alert('Invitation link copied to clipboard!');
   } catch (err) {
     console.error('Failed to copy link:', err);
     alert('Failed to copy link. Please try again.');
   }
 };


 // Final submit
 const handleSubmit = async () => {
   if (!user || !user.uid) {
     raiseError('Your session expired — please reload and sign in again.');
     return;
   }

   setLoading(true);
   setError(null);

   try {
     // 1) Basic date/time checks
     if (!eventDate) {
       setError('Please select a valid event date.');
       setLoading(false);
       return;
     }
     const mergedDateStr = startTime ? `${eventDate}T${startTime}` : `${eventDate}T00:00`;
     const eventDateObject = new Date(mergedDateStr);
     if (isNaN(eventDateObject.getTime())) {
       setError('Invalid event date or time. Please check your inputs.');
       setLoading(false);
       return;
     }

     // 2) Validate reminder date
     let reminderDateObject: Date | null = null;
     if (reminderDate) {
       const rDate = new Date(reminderDate);
       if (isNaN(rDate.getTime())) {
         setError('Invalid reminder date/time. Please check your inputs.');
         setLoading(false);
         return;
       }
       reminderDateObject = rDate;
     }

     // 3) Upload image and video if provided
     let imageUrl = mainImageUrl;
     let videoUrl = eventVideoUrl;

     if (mainImage && !mainImageUrl) {
       try {
         const fileName = `${Date.now()}_${mainImage.name}`;
         imageUrl = await uploadFileToStorage(mainImage, fileName);
         setMainImageUrl(imageUrl);
       } catch (uploadError) {
         console.error('Error uploading image:', uploadError);
         imageUrl = '';
       }
     }

     if (eventVideo && !eventVideoUrl) {
       try {
         const fileName = `${Date.now()}_${eventVideo.name}`;
         videoUrl = await uploadFileToStorage(eventVideo, fileName);
         setEventVideoUrl(videoUrl);
       } catch (uploadError) {
         console.error('Error uploading video:', uploadError);
         videoUrl = '';
       }
     }

     // 4) Merge custom data
     const mergedCustomData = {
       ...customData,
       eventTitle: title,
       address: location_state,
       sTime: startTime,
       eTime: endTime,
       mainImageUrl: imageUrl ?? '',
       eventVideoUrl: videoUrl ?? '',
       musicId: selectedMusicId ?? '',
       musicUrl: selectedMusicUrl ?? '',
       musicName: selectedMusicName ?? '',
     };

     // 5) Create or Update event document
     let finalEventId: string;
    
     if (isEditMode && editEventId) {
       // UPDATE existing event
       const eventDocRef = doc(db, 'events', editEventId);
       await updateDoc(eventDocRef, {
         title,
         description,
         location: location_state,
         eventDate: eventDateObject,
         eventTime: startTime,
         endTime: endTime,
         reminderDate: reminderDateObject,
         templateId,
         customData: mergedCustomData,
         musicId: selectedMusicId,
         musicUrl: selectedMusicUrl,
         musicName: selectedMusicName,
         updatedAt: serverTimestamp(),
       });
      
       finalEventId = editEventId;
       setCreatedEventId(editEventId);
      
       // Navigate back to event details after update
       navigate(`/event/${editEventId}`);
      
     } else {
       // CREATE new event
       const eventRef = await addDoc(collection(db, 'events'), {
         userId: user.uid,
         title,
         description,
         location: location_state,
         eventDate: eventDateObject,
         eventTime: startTime,
         endTime: endTime,
         reminderDate: reminderDateObject,
         templateId,
         customData: mergedCustomData,
         musicId: selectedMusicId,
         musicUrl: selectedMusicUrl,
         musicName: selectedMusicName,
         createdAt: serverTimestamp(),
         reminderSent: false,
       });

       // 6) Create anonymous invitation (only for new events)
       const anonymousCode =
         Math.random().toString(36).substring(2, 8) +
         Math.random().toString(36).substring(2, 8);
       const invitationRef = await addDoc(
         collection(db, 'invitations'),
         {
           eventId: eventRef.id,
           recipientName: 'Anonymous',
           uniqueCode: anonymousCode,
           viewsCount: 0,
           acceptedCount: 0,
           declinedCount: 0,
           peopleCount: 0,
           status: 'sent',
           sentAt: serverTimestamp(),
         }
       );
       const invitationUrl = `/invite/${anonymousCode}?id=${invitationRef.id}`;
       await updateDoc(eventRef, { anonymousInvitationLink: invitationUrl });

       finalEventId = eventRef.id;
       setCreatedEventId(eventRef.id);
      
       if (isAnonymous && !isEditMode) {
         setCurrentStep(Step.Registration);
       } else {
         // MODIFICATION: Open sales modal instead of going to the next step
         setIsSalesModalOpen(true);
       }
     }
    
   } catch (err: any) {
     console.error('Create/Update event error:', err);
     setError(err.message || 'Failed to save event. Please try again.');
   } finally {
     setLoading(false);
   }
 };


 // Sync customData with actual values (not placeholder) after user fills them
 useEffect(() => {
   // Only update if we have actual user data, not placeholders
   if (title || location_state || startTime || endTime || eventDate) {
     setCustomData(prev => ({
       ...prev,
       eventTitle: title || prev.eventTitle,
       address: location_state || prev.address,
       sTime: startTime || prev.sTime,
       eTime: endTime || prev.eTime,
       eventDate: eventDate ? formatEventDate(eventDate) : prev.eventDate,
       mainImageUrl: mainImagePreview || mainImageUrl || prev.mainImageUrl,
       eventVideoUrl: eventVideoPreview || eventVideoUrl || prev.eventVideoUrl,
       musicId: selectedMusicId ?? prev.musicId,
       musicUrl: selectedMusicUrl ?? prev.musicUrl,
       musicName: selectedMusicName ?? prev.musicName,
     }));
   }
 }, [
   title,
   location_state,
   startTime,
   endTime,
   eventDate,
   mainImagePreview,
   mainImageUrl,
   eventVideoPreview,
   eventVideoUrl,
   selectedMusicId,
   selectedMusicUrl,
   selectedMusicName
 ]);


 // Mobile-only fixes for preview steps
 useEffect(() => {
  const sheet = document.createElement('style');
  sheet.textContent = `
    /* Flip card styles */
    .flip-container {
      perspective: 1000px;
      width: 100%;
      height: var(--phoneHeight, 640px);
      min-height: 500px;
    }
    .flip-inner {
      position: relative;
      width: 100%;
      height: 100%;
      transition: transform 0.6s;
      transform-style: preserve-3d;
    }
    .flip-container.is-flipped .flip-inner {
      transform: rotateY(180deg);
    }
    .flip-face {
      position: absolute;
      width: 100%;
      height: 100%;
      backface-visibility: hidden;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      display: flex;
      flex-direction: column;
    }
    .flip-back {
      transform: rotateY(180deg);
    }
    .panel-body {
      padding: 1.5rem;
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
    }
    .why-card {
      text-align: center;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .pill-time {
      background: rgba(255, 255, 255, 0.2);
      padding: 0.5rem 1rem;
      border-radius: 20px;
      display: inline-block;
      margin-bottom: 1rem;
      font-size: 0.9rem;
    }
    .why-title {
      font-size: 2rem;
      font-weight: bold;
      margin-bottom: 1rem;
    }
    .why-bullets {
      list-style: none;
      padding: 0;
      margin: 1rem 0;
    }
    .why-bullets li {
      margin: 0.5rem 0;
      font-size: 1.1rem;
    }
    .trust-row {
      display: flex;
      justify-content: space-around;
      margin: 1rem 0;
      font-size: 0.8rem;
      opacity: 0.8;
    }
    .edit-panel {
      height: 100%;
      overflow-y: auto;
    }

    /* TIGHTER SPACING FOR EDIT PANEL */
    .section-heading {
      font-size: 1.5rem;
      font-weight: bold;
      margin-bottom: 8px; /* REDUCED */
    }
    .spark-label {
      display: block;
      font-weight: bold;
      margin: 12px 0 4px 0; /* REDUCED */
    }
    .date-time-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
      margin: 8px 0; /* REDUCED */
    }
    .date-time-grid.tight {
      grid-template-columns: 1fr;
    }
    .media-actions {
      display: flex;
      gap: 0.5rem;
      margin: 16px 0 0 0; /* ADJUSTED */
      flex-wrap: wrap;
    }

    /* FOOTER BUTTON LAYOUT FIXES */
    .main-cta {
       margin-top: auto; /* Pushes footer to the bottom of the card */
       padding-top: 1rem;
       display: flex;
       gap: 1rem;
       justify-content: space-between;
       align-items: center;
       flex-wrap: wrap;
    }
    .main-cta-right {
       display: flex;
       align-items: center;
       gap: 0.75rem; /* Adds space between desktop buttons */
       flex-wrap: wrap;
    }
    
    /* Modal styles */
    .spark-modal-overlay {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex; align-items: center; justify-content: center;
      z-index: 1000;
    }
    .spark-modal {
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      border-radius: 12px;
      max-width: 500px; width: 90%;
      max-height: 80vh; overflow: hidden; color: white;
    }
    .spark-modal-header {
      padding: 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      display: flex; justify-content: space-between; align-items: center;
    }
    .spark-modal-header h3 { margin: 0; font-size: 1.2rem; }
    .spark-modal-close {
      background: none; border: none; color: white;
      font-size: 1.5rem; cursor: pointer;
    }
    .spark-modal-body {
      padding: 1rem; overflow-y: auto;
      max-height: calc(80vh - 80px);
    }
    .media-modal { text-align: center; }
    .media-preview {
      max-width: 100%; max-height: 200px;
      border-radius: 8px; margin-bottom: 1rem;
    }
    .media-placeholder {
      width: 100%; height: 200px;
      border: 2px dashed rgba(255, 255, 255, 0.3);
      border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 1rem; color: rgba(255, 255, 255, 0.6);
    }
    .spark-modal-actions {
      margin-top: 1rem; display: flex; justify-content: center;
    }
    .music-modal { max-height: 400px; overflow-y: auto; }
    
    /* General Layout */
    .preview-grid {
      display: flex;
      gap: 3rem;
      justify-content: center;
      flex-wrap: wrap;
      margin-bottom: 2rem;
    }
    .preview-phone {
      min-width: 280px; flex: 0 0 auto;
      width: min(360px, 90vw);
    }
    .flip-container {
      min-width: 280px; flex: 1 1 340px;
      max-width: 480px;
    }

    /* MOBILE & TABLET OVERRIDES */
    @media (max-width: 768px) {
      .preview-grid {
        flex-direction: column;
        align-items: center;
        gap: 1.5rem;
      }
      .flip-container { min-height: 450px; }
      
     /* MOBILE BUTTON STACKING FIX */
      .main-cta {
        position: relative; /* Puts the footer back in the normal page flow */
        margin-top: 1.5rem; /* Adds space above the buttons */
        flex-direction: column-reverse; /* Puts NEXT/CUSTOMIZE at the bottom */
        gap: 0.75rem;
        width: 100%;
      }
      .main-cta-left, .main-cta-right {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 0.75rem;
      }
      .main-cta-right {
        flex-direction: column-reverse; /* Ensures NEXT/CUSTOMIZE is last */
      }
    }
  `;
  document.head.appendChild(sheet);
  return () => sheet.remove();
}, []);

 // Update preview key when in preview steps
 useEffect(() => {
   if (currentStep === Step.TemplatePreview) {
     stopAllMediaPlayback();
    
     // Force a complete reset of the preview
     setPreviewKey('');
     setIsPreviewReady(false);
    
     const timeout = setTimeout(() => {
       const newKey = createStableKey(
         templateId,
         selectedMusicId,
         mainImagePreview || mainImageUrl || customData.mainImageUrl,
         eventVideoPreview || eventVideoUrl || customData.eventVideoUrl
       );
      
       setPreviewKey(newKey);
       setIsPreviewReady(true);
     }, 200); // Increased delay to ensure cleanup


     return () => clearTimeout(timeout);
   }
 }, [currentStep, templateId, selectedMusicId, mainImagePreview, mainImageUrl, eventVideoPreview, eventVideoUrl, customData]);


 // Fetch template data when templateId changes
 useEffect(() => {
   const fetchTemplate = async () => {
     if (!templateId) return;
    
     try {
       const q = query(
         collection(db, 'templates'),
         where('__name__', '==', templateId)
       );
       const snapshot = await getDocs(q);
      
       if (!snapshot.empty) {
         const templateData = {
           id: snapshot.docs[0].id,
           ...snapshot.docs[0].data()
         } as TemplateType;
         setActiveTemplate(templateData);
       }
     } catch (error) {
       console.error('Error fetching template:', error);
     }
   };
  
   fetchTemplate();
 }, [templateId]);


 // Fetch invitation link when reaching SendInvitations
 useEffect(() => {
   if (currentStep === Step.SendInvitations && (createdEventId || editEventId)) {
     const fetchInvitationLink = async () => {
       try {
         const eventRef = doc(db, 'events', createdEventId || editEventId!);
         const eventDoc = await getDoc(eventRef);
        
         if (eventDoc.exists()) {
           const data = eventDoc.data();
           const link = data.anonymousInvitationLink;
          
           if (link) {
             setAnonymousInvitationLink(link);
           }
         }
       } catch (error) {
         console.error('Error fetching invitation link:', error);
       }
     };
    
     fetchInvitationLink();
   }
 }, [currentStep, createdEventId, editEventId]);


 // NEW: Handle templateId from URL parameter
 useEffect(() => {
   if (urlTemplateId && !templateId) {
     // Auto-select template from URL parameter
     setTemplateId(urlTemplateId);
    
     // Set up music placeholder for this template
     const libraryKey = `template-${urlTemplateId}`;
     const firstTrack = musicLibraries[libraryKey]?.[0];
    
     if (firstTrack) {
       setSelectedMusicId(firstTrack.id);
       setSelectedMusicUrl(firstTrack.url);
       setSelectedMusicName(firstTrack.name);
      
       setCustomData(prev => ({
         ...prev,
         musicId: firstTrack.id,
         musicUrl: firstTrack.url,
         musicName: firstTrack.name,
       }));
     } else {
       setSelectedMusicId(null);
       setSelectedMusicUrl('');
       setSelectedMusicName('');
       setCustomData(prev => ({
         ...prev,
         musicId: '',
         musicUrl: '',
         musicName: '',
       }));
     }
   }
 }, [urlTemplateId, templateId, musicLibraries]);

 // CRITICAL FIX: Load existing event data when editEventId is present
 useEffect(() => {
   const loadExistingEvent = async () => {
     if (!editEventId) return;
    
     setLoading(true);
     setError(null);
    
     try {
       const eventRef = doc(db, 'events', editEventId);
       const eventSnap = await getDoc(eventRef);
      
       if (!eventSnap.exists()) {
         setError('Event not found');
         setLoading(false);
         return;
       }
      
       const eventData = eventSnap.data();
       setOriginalEventData(eventData);
      
       // Populate all state with existing event data
       setTitle(eventData.title || '');
       setDescription(eventData.description || '');
       setLocation_state(eventData.location || '');
      
       // Handle event date
       if (eventData.eventDate) {
         const date = eventData.eventDate.toDate ? eventData.eventDate.toDate() : new Date(eventData.eventDate);
         const dateStr = date.toISOString().split('T')[0];
         setEventDate(dateStr);
       }
      
       setStartTime(eventData.eventTime || '');
       setEndTime(eventData.endTime || '');
      
       // Handle reminder date
       if (eventData.reminderDate) {
         const rDate = eventData.reminderDate.toDate ? eventData.reminderDate.toDate() : new Date(eventData.reminderDate);
         const rDateStr = rDate.toISOString().split('T')[0];
         setReminderDate(rDateStr);
       }
      
       // Set template info
       if (eventData.templateId) {
         setTemplateId(eventData.templateId);
       }
      
       // Set music info
       if (eventData.musicId) {
         setSelectedMusicId(eventData.musicId);
         setSelectedMusicUrl(eventData.musicUrl || '');
         setSelectedMusicName(eventData.musicName || '');
       }
      
       // Set media URLs
       if (eventData.customData?.mainImageUrl) {
         setMainImageUrl(eventData.customData.mainImageUrl);
         setMainImagePreview(eventData.customData.mainImageUrl);
       }
      
       if (eventData.customData?.eventVideoUrl) {
         setEventVideoUrl(eventData.customData.eventVideoUrl);
         setEventVideoPreview(eventData.customData.eventVideoUrl);
       }
      
       // Set custom data
       if (eventData.customData) {
         setCustomData(eventData.customData);
       }
      
       // Set reminder settings
       if (eventData.reminderNoResponse) {
         setReminderNoResponse(eventData.reminderNoResponse);
       }
       if (eventData.reminderAll) {
         setReminderAll(eventData.reminderAll);
       }
      
       // Set the created event ID for editing
       setCreatedEventId(editEventId);
      
       // Get anonymous invitation link
       if (eventData.anonymousInvitationLink) {
         setAnonymousInvitationLink(eventData.anonymousInvitationLink);
       }

       // AUTO-FLIP to edit panel when in edit mode
       if (currentStep === Step.TemplatePreview) {
         setRightSideFlipped(true);
       }
      
     } catch (err) {
       console.error('Error loading event:', err);
       setError('Failed to load event data. Please try again.');
     } finally {
       setLoading(false);
     }
   };
  
   loadExistingEvent();
 }, [editEventId]);


 /* ----------------------------------------------------------
    Renders for each step
 ---------------------------------------------------------- */


 // Step 1: Template Selection
 if (currentStep === Step.TemplateSelection) {
   return (
     <SparkLayout>
       <SparkCard className="overflow-auto max-h-[70vh] w-5/6 max-w-3xl mb-16">
         <SparkHeading>Choose Your Invitation</SparkHeading>
         <p className="text-center mb-6 text-lg">
           Select a template to see how your invitation will look
         </p>
         <TemplateSelector
             selectedTemplate={templateId || urlTemplateId}
             onSelectTemplate={handleSelectTemplate}
             customData={customData}
             onCustomDataChange={handleCustomDataChange}
             isEditMode={isEditMode}
             defaultSearchQuery={urlSearchTerm}   // NEW: pre-fill "pool" if present
           />
         <SparkFooter className="mt-6">
           <SparkButton
             variant="secondary"
             onClick={() => navigate('/dashboard')}
           >
             CANCEL
           </SparkButton>
           <SparkButton
             onClick={goNext}
             disabled={!templateId}
           >
             NEXT
           </SparkButton>
         </SparkFooter>
       </SparkCard>
     </SparkLayout>
   );
 }


 // Step 2: Template Preview â€” two columns + global footer (buttons at bottom of the main card)
 if (currentStep === Step.TemplatePreview) {
   const isFancy = activeTemplate?.fancy === true;
   const openRealPreview = handleTemplatePreviewInNewTab;


   return (
     <SparkLayout>
        <div style={{ marginTop: '-1rem', marginBottom: '2rem' }}>
          <TipCard message="Not ready to add media? No worries! You can add it later." />
        </div>
          <SparkCard className="preview-card">
            <SparkHeading className="text-center mb-2">
              Preview Your Invitation Template
            </SparkHeading>
            <p className="text-center mb-6 text-lg">
              This is how your invitation will look. Next, you'll customize it with your event details.
            </p>


         {/* Two-column content */}
         <div
           className="preview-grid"
           style={{ ['--phoneHeight' as any]: `${phoneHeight}px` }}
         >
           {/* LEFT: phone preview */}
           <div className="preview-phone" ref={phoneRef}>
           {isFancy ? (
               <div style={{ width: '100%', maxWidth: 600 }}>
                 {activeTemplate?.id === 'halloweenhorrorTheme' || activeTemplate?.name === 'halloweenhorrorTheme' ? (
                   <MagicalPreviewDisplay
                     onOpenPreview={openRealPreview}
                     buttonText="OPEN FULL PREVIEW"
                     showBackMessage
                   />
                 ) : (
                   <FancyInvitationPreview
                     template={activeTemplate}
                     data={customData}
                     onError={(err) => {
                       console.error('Fancy preview error:', err);
                       setError('Failed to load preview');
                     }}
                   />
                 )}
               </div>
             ) : (
               <div className="phone-preview-frame" style={{ display: 'flex', justifyContent: 'center' }}>
                 <div className="phone-safe-pad" style={{ width: 'min(360px, 90vw)' }}>
                   {activeTemplate && previewKey && isPreviewReady ? (
                     <VideoThemePlayer key={previewKey} template={activeTemplate} data={customData} mode="preview" />
                   ) : (
                     <div className="flex items-center justify-center h-full text-white">Loading previewâ€¦</div>
                   )}
                 </div>
               </div>
             )}
           </div>


           {/* RIGHT: flip card */}
            <div
              className={`flip-container ${rightSideFlipped ? 'is-flipped' : ''}`}
              style={{ ['--phoneHeight' as any]: `${phoneHeight}px` }}
            >
              <div className="flip-inner">
                {/* FRONT: WHY SPARK */}
                <div className="flip-face flip-front">
                  <div className="panel-body why-panel">
                    {/* Centered, bold pill */}
                    <div className="pill-time" role="note" aria-label="Customize in about two minutes">
                      Customize in ~1 minute
                    </div>

                    {/* Large centered title (no stray icon) */}
                    <h2 className="why-title">WHY SPARK</h2>

                    {/* 3 benefit bullets with circular checks */}
                    <ul className="why-bullets">
                      <li><span className="why-check"></span><span>Easily text, email, or share your link any way you want</span></li>
                      <li><span className="why-check"></span><span>Easily collect RSVPs in real time</span></li>
                      <li><span className="why-check"></span><span>Easily poke unresponsive guests</span></li>
                      <li><span className="why-check"></span><span>Hands on support, email natalie@sparkinvites.com</span></li>
                    </ul>

                    {/* Separate glass trust mini-card with simple checks */}
                    <div className="trust-card" aria-label="Trust signals">
                      <div className="trust-item"><span className="trust-tick"></span><span>Use code 80OFF for 80% off at checkout</span></div>
                      <div className="trust-item"><span className="trust-tick"></span><span>Hundreds of happy parties</span></div>
                      <div className="trust-item"><span className="trust-tick"></span><span>Invites that really WOW</span></div>
                    </div>
                  </div>
                </div>

                {/* BACK: EDIT PANEL (markup unchanged; spacing handled in CSS) */}
                <div className="flip-face flip-back">
                  <div className="panel-body edit-panel">
                    <h3 className="section-heading">Event Details</h3>

                    <label className="spark-label">Event Name</label>
                    <SparkInput
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Suzy's 3rd Birthday"
                    />

                    <label className="spark-label">Event Location</label>
                    <SparkInput
                      type="text"
                      value={location_state}
                      onChange={(e) => setLocation_state(e.target.value)}
                      placeholder="123 Party Ln, Boca Raton"
                    />

                    {/* Date on its own line */}
                    <div className="date-time-grid tight">
                      <div>
                        <label className="spark-label">Date</label>
                        <SparkInput type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
                      </div>
                    </div>

                    {/* Start / End in a compact two-up */}
                    <div className="date-time-grid compact-2">
                      <div>
                        <label className="spark-label">Start Time</label>
                        <SparkInput type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                      </div>
                      <div>
                        <label className="spark-label">End Time</label>
                        <SparkInput type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                      </div>
                    </div>

                    <div className="media-actions media-3up">
                      <SparkButton className="media-btn" variant="secondary" onClick={() => setPhotoModalOpen(true)}>
                        {mainImage || mainImageUrl ? 'âœ“ Photo Added' : '+ Add Photo'}
                      </SparkButton>
                      <SparkButton className="media-btn" variant="secondary" onClick={() => setVideoModalOpen(true)}>
                        {eventVideo || eventVideoUrl ? 'âœ“ Video Added' : '+ Add Video'}
                      </SparkButton>
                      <SparkButton className="media-btn" variant="secondary" onClick={() => setMusicModalOpen(true)}>
                        {selectedMusicId ? 'âœ“ Song Selected' : '+ Add Song'}
                      </SparkButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
         </div>


         {/* GLOBAL FOOTER â€” buttons always at bottom of the main card */}
          <div className="main-cta">
            <div className="main-cta-left">
              <SparkButton variant="secondary" onClick={goBack}>BACK</SparkButton>
            </div>
            <div className="main-cta-right">
              <SparkButton variant="secondary" onClick={openRealPreview} className="btn-preview">
                {isFancy ? 'OPEN FULL PREVIEW' : 'SEE REAL INVITATION'}
              </SparkButton>

              {!rightSideFlipped ? (
                <SparkButton onClick={() => setRightSideFlipped(true)}>
                  CUSTOMIZE THIS INVITATION
                </SparkButton>
              ) : (
                <SparkButton onClick={() => setCurrentStep(Step.Review)}>
                  NEXT
                </SparkButton>
              )}
            </div>
          </div>

         {/* Modals */}
         {isPhotoModalOpen && (
           <MediaImageModal
             initialPreview={mainImagePreview}
             onClose={() => setPhotoModalOpen(false)}
             onChange={(file, previewUrl) => { setMainImage(file); setMainImagePreview(previewUrl); }}
           />
         )}
         {isVideoModalOpen && (
           <MediaVideoModal
             initialPreview={eventVideoPreview}
             onClose={() => setVideoModalOpen(false)}
             onValidateDuration={getVideoDuration}
             onChange={(file, previewUrl) => { setEventVideo(file); setEventVideoPreview(previewUrl); }}
           />
         )}
         {isMusicModalOpen && (
           <MusicSelectModal
             templateId={templateId}
             selectedMusicId={selectedMusicId}
             onClose={() => setMusicModalOpen(false)}
             onSelect={(id, url, name) => {
               setSelectedMusicId(id);
               setSelectedMusicUrl(url);
               setSelectedMusicName(name);
               setCustomData((p) => ({ ...p, musicId: id || '', musicUrl: url || '', musicName: name || '' }));
             }}
           />
         )}
       </SparkCard>
     </SparkLayout>
   );
 }


 // Step 3: Final Review & Create
 if (currentStep === Step.Review) {
   return (
      <>
      <SparkLayout>
        <TipCard message="Not ready to add media? No worries! You can add it later." />
        <SparkCard className="review-card">
           <SparkHeading className="text-center mb-8">
             Review &amp; Create Invitation
           </SparkHeading>


           {error && (
             <div className="spark-message spark-message--error mb-6">
               {error}
             </div>
           )}


           {/* TWO-COLUMN GRID */}
           <div className="review-grid">
             {/* Event Details */}
             <div className="review-section">
               <h3 className="section-heading">Event Details</h3>
               <dl className="detail-list">
                 <div>
                   <dt>Event</dt>
                   <dd>{title || "â€”"}</dd>
                 </div>
                 <div>
                   <dt>Date</dt>
                   <dd>
                     {eventDate
                       ? new Date(eventDate).toLocaleDateString()
                       : "â€”"}
                   </dd>
                 </div>
                 <div>
                   <dt>Time</dt>
                   <dd>
                     {startTime
                       ? `${startTime}${endTime ? ` â€” ${endTime}` : ""}`
                       : "â€”"}
                   </dd>
                 </div>
                 <div>
                   <dt>Location</dt>
                   <dd>{location_state || "â€”"}</dd>
                 </div>
               </dl>
             </div>


             {/* Media */}
             <div className="review-section">
               <h3 className="section-heading">Media</h3>
               <ul className="media-list">
                 <li className={mainImage ? "" : "missing"}>
                   Photo&nbsp;{mainImage ? "Uploaded" : "Not added"}
                 </li>
                 <li className={eventVideo ? "" : "missing"}>
                   Video&nbsp;{eventVideo ? "Uploaded" : "Not added"}
                 </li>
                 <li className={selectedMusicId ? "" : "missing"}>
                   {selectedMusicId
                     ? `â™« ${selectedMusicName}`
                     : "No music selected"}
                 </li>
               </ul>
             </div>
           </div>


           {/* ACTION BAR */}
           <SparkFooter className="cta-bar">
             <SparkButton variant="secondary" onClick={() => setCurrentStep(Step.TemplatePreview)}>
               Back
             </SparkButton>
             <SparkButton onClick={handleSubmit} disabled={loading}>
               {loading ? "Creatingâ€¦" : "Create Invitation"}
             </SparkButton>
           </SparkFooter>
         </SparkCard>
       </SparkLayout>


       {/* MODIFICATION: Render the sales modal when its state is true */}
       {isSalesModalOpen && createdEventId && (
           <UnlockSalesModal
               eventId={createdEventId}
               initiallyOpen={true}
               currentStep={currentStep}
               onClose={() => {
                   setIsSalesModalOpen(false);
                   setCurrentStep(Step.AddGuests);
               }}
           />
       )}
     </>
   );
 }


 // Step 4: Add Guests (formerly ChooseRecipients; occurs AFTER creation)
 if (currentStep === Step.AddGuests) {
   return (
     <SparkLayout>
       <TipCard message="Don't want to add contacts? No problem! Just copy the invitation link and send it to anyone, any way you like." />
       <SparkCard className="overflow-auto max-h-[70vh] w-5/6 max-w-3xl mb-16">
         <SparkHeading className="text-center">Who's invited?</SparkHeading>
         <div className="w-full">
           <ChooseRecipients
             recipients={recipients}
             setRecipients={setRecipients}
             onNext={() => setCurrentStep(Step.SendInvitations)}
             onBack={() => setCurrentStep(Step.Review)}
           />
         </div>
       </SparkCard>
     </SparkLayout>
   );
 }


 // Step 5: Send Invitations
 if (currentStep === Step.SendInvitations && createdEventId) {
   // Check if we have a valid event ID for paywall check
   if (!eventId) {
     return (
       <SparkLayout>
         <SparkCard>
           <div className="loading-container flex items-center justify-center h-40">
             <div className="text-center">
               <p className="text-xl">Loading event...</p>
             </div>
           </div>
         </SparkCard>
       </SparkLayout>
     );
   }


   if (paywallLoading) {
     return (
       <SparkLayout>
         <SparkCard>
           <div className="loading-container flex items-center justify-center h-40">
             <div className="text-center">
               <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-2" />
               <p className="text-xl">Checking Status...</p>
             </div>
           </div>
         </SparkCard>
       </SparkLayout>
     );
   }


   if (unlocked) {
     // UNLOCKED STATE: Show the original GuestList component
     return (
       <SparkLayout>
         <SparkCard>
           <SparkHeading>Send Invitations</SparkHeading>
           <GuestList
             eventId={createdEventId}
             recipients={recipients}
             onSendComplete={() => setCurrentStep(Step.ReminderSettings)}
             onSkip={() => setCurrentStep(Step.ReminderSettings)}
             onBack={() => setCurrentStep(Step.AddGuests)}
             invitationLink={anonymousInvitationLink}
             onCopyLink={handleCopyInvitationLink}
           />
         </SparkCard>
       </SparkLayout>
     );
   }


   // LOCKED STATE: Show the paywall button with SKIP option
   return (
     <SparkLayout>
       <SparkCard>
         <SparkHeading>Unlock Your Invitation</SparkHeading>
         <p style={{ textAlign: 'center', margin: '1rem 0', lineHeight: '1.5' }}>
           Your invitation is ready. Unlock distribution to send it to your guests and get a shareable link.
         </p>
         <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
           <div style={{ width: 'min(400px, 100%)' }}>
           <UnlockPaywallButton
             eventId={createdEventId}
             currentStep={currentStep}
           />
           </div>
         </div>
         <SparkFooter>
           <SparkButton
             variant="secondary"
             onClick={() => setCurrentStep(Step.AddGuests)}
           >
             BACK
           </SparkButton>
           <SparkButton
             onClick={() => setCurrentStep(Step.ReminderSettings)}
           >
             SKIP FOR NOW
           </SparkButton>
         </SparkFooter>
       </SparkCard>
     </SparkLayout>
   );
 }


 // Step 6: Reminder Settings
 if (currentStep === Step.ReminderSettings) {
   return (
     <SparkLayout>
       <SparkCard>
         <SparkHeading>Set Up Reminders</SparkHeading>


         {error && (
           <div className="spark-message spark-message--error mb-6">{error}</div>
         )}


         <div className="space-y-8">
           <div>
             <label className="block text-base font-semibold mb-2 text-white">
               Send reminder to guests who haven't responded
             </label>
             <select
               className="spark-input"
               value={reminderNoResponse}
               onChange={(e) => setReminderNoResponse(e.target.value)}
             >
               <option value="">No automatic reminders</option>
               <option value="1d">1 day before event</option>
               <option value="3d">3 days before event</option>
               <option value="1w">1 week before event</option>
               <option value="2w">2 weeks before event</option>
             </select>
           </div>


           <div>
             <label className="block text-base font-semibold mb-2 text-white">
               Send event reminder to all guests
             </label>
             <select
               className="spark-input"
               value={reminderAll}
               onChange={(e) => setReminderAll(e.target.value)}
             >
               <option value="">No reminder</option>
               <option value="0d">On the day of the event</option>
               <option value="1d">1 day before event</option>
               <option value="3d">3 days before event</option>
               <option value="1w">1 week before event</option>
             </select>
           </div>
         </div>


         <SparkFooter className="mt-8">
           <SparkButton
             variant="secondary"
             onClick={() => setCurrentStep(Step.SendInvitations)}
           >
             BACK
           </SparkButton>


           <SparkButton
             onClick={async () => {
               if (!createdEventId) return;
               try {
                 const eventDocRef = doc(db, "events", createdEventId);
                 await updateDoc(eventDocRef, {
                   reminderNoResponse,
                   reminderAll,
                 });
               } catch (err) {
                 console.error("Error saving reminders:", err);
               } finally {
                 navigate(`/event/${createdEventId}`);
               }
             }}
           >
             FINISH
           </SparkButton>
         </SparkFooter>
       </SparkCard>
     </SparkLayout>
   );
 }


 // Step 7: Registration
 if (currentStep === Step.Registration && createdEventId) {
   return (
     <SparkLayout>
       <SparkCard>
         <SparkHeading>
           {isLoginMode ? "Log In to Your Account" : "Create Your Account"}
         </SparkHeading>


         <div className="mb-6 text-center">
           <p className="text-xl">
             Your event has been created!{" "}
             {isLoginMode
               ? "Log in to send invitations and manage your event."
               : "Create an account to send invitations and manage your event."}
           </p>
         </div>


         {error && <div className="error-message">{error}</div>}


         {!isLoginMode && (
           <div className="w-full mb-6">
             <label className="block text-xl font-bold mb-2">Display Name</label>
             <SparkInput
               type="text"
               value={regDisplayName}
               onChange={(e) => setRegDisplayName(e.target.value)}
               placeholder="Your name"
             />
           </div>
         )}


         <div className="w-full mb-6">
           <label className="block text-xl font-bold mb-2">Email</label>
           <SparkInput
             type="email"
             value={regEmail}
             onChange={(e) => setRegEmail(e.target.value)}
             placeholder="Your email"
             required
           />
         </div>


         <div className="w-full mb-6">
           <label className="block text-xl font-bold mb-2">Password</label>
           <SparkInput
             type="password"
             value={regPassword}
             onChange={(e) => setRegPassword(e.target.value)}
             placeholder={isLoginMode ? "Enter your password" : "Create a password"}
             required
           />
         </div>


         <SparkFooter>
           <SparkButton
             onClick={async () => {
               try {
                 setLoading(true);
                 setError(null);


                 if (isAnonymous && !isLoginMode) {
                   await convertAnonymousAccount(regEmail, regPassword, regDisplayName);
                 } else {
                   await login(regEmail, regPassword);


                   if (auth.currentUser?.uid !== user?.uid) {
                     const claim = httpsCallable(getFunctions(), "claimEventOwnership");
                     await claim({ eventId: createdEventId });
                   }
                 }


                 setCurrentStep(Step.SendInvitations);
               } catch (err: any) {
                 console.error("Authentication error:", err);
                 setError(err.message || "Authentication failed. Please try again.");
               } finally {
                 setLoading(false);
               }
             }}
             disabled={
               loading ||
               !regEmail ||
               !regPassword ||
               (!isLoginMode && !regDisplayName)
             }
           >
             {loading ? (
               <>
                 <span className="inline-block animate-pulse">
                   {isLoginMode ? "LOGGING IN" : "CREATING ACCOUNT"}
                 </span>
                 <span className="inline-block animate-bounce ml-1">.</span>
                 <span className="inline-block animate-bounce ml-1 animation-delay-200">.</span>
                 <span className="inline-block animate-bounce ml-1 animation-delay-400">.</span>
               </>
             ) : isLoginMode ? (
               "LOG IN"
             ) : (
               "COMPLETE SIGN-UP"
             )}
           </SparkButton>
         </SparkFooter>


         <div className="mt-6 text-center">
           <p className="mb-2">
             {isLoginMode
               ? "Don't have an account yet?"
               : "Already have an account?"}
           </p>
           <SparkButton
             variant="secondary"
             onClick={() => {
               setIsLoginMode(!isLoginMode);
               setError(null);
             }}
             className="spark-button-small"
           >
             {isLoginMode ? "CREATE ACCOUNT" : "LOG IN"}
           </SparkButton>
         </div>


         {!isLoginMode && (
           <>
             <style>{`
               .legal-link,
               .legal-link:visited {
                 color: rgba(255, 255, 255, 0.60) !important;
                 text-decoration: none !important;
               }
               .legal-link:hover {
                 color: rgba(255, 255, 255, 0.90) !important;
               }
             `}</style>


             <div className="mt-6 text-center text-xs">
               By signing up, you agree to our{' '}
               <a
                 href="/terms"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="legal-link"
               >
                 Terms&nbsp;and&nbsp;Conditions
               </a>{' '}
               and{' '}
               <a
                 href="/privacy"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="legal-link"
               >
                 Privacy&nbsp;Policy
               </a>.
             </div>
           </>
         )}
       </SparkCard>
     </SparkLayout>
   );
 }


 // Fallback render
 return (
   <SparkLayout>
     <SparkCard>
       <div className="loading-container flex items-center justify-center h-40">
         <div className="text-center">
           <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black mx-auto mb-2" />
           <p className="text-xl">Loading...</p>
         </div>
       </div>
     </SparkCard>
   </SparkLayout>
 );
};


export default CreateEventFromTemplate;